import React, { createContext, useContext, useState, useCallback } from 'react';
import AlertModal from './AlertModal';

const AlertContext = createContext();

/**
 * AlertProvider Component
 * 
 * Provides alert/modal functionality throughout the application
 * Wrap your app with this provider to use the useAlert hook
 */
export const AlertProvider = ({ children }) => {
  const [alertState, setAlertState] = useState({
    open: false,
    type: 'info',
    title: '',
    message: '',
    confirmText: '',
    cancelText: 'Cancel',
    onConfirm: null,
    onCancel: null,
    showCancel: undefined
  });

  // Close the modal
  const closeAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, open: false }));
  }, []);

  // Show alert with custom options
  const showAlert = useCallback((options) => {
    setAlertState({
      open: true,
      type: options.type || 'info',
      title: options.title || '',
      message: options.message || '',
      confirmText: options.confirmText || '',
      cancelText: options.cancelText || 'Cancel',
      onConfirm: options.onConfirm || null,
      onCancel: options.onCancel || null,
      showCancel: options.showCancel
    });
  }, []);

  // Convenience method for success alerts
  const showSuccess = useCallback((message, title, onConfirm) => {
    showAlert({
      type: 'success',
      title: title || 'Success',
      message,
      onConfirm
    });
  }, [showAlert]);

  // Convenience method for error alerts
  const showError = useCallback((message, title, onConfirm) => {
    showAlert({
      type: 'error',
      title: title || 'Error',
      message,
      onConfirm
    });
  }, [showAlert]);

  // Convenience method for warning alerts
  const showWarning = useCallback((message, title, onConfirm) => {
    showAlert({
      type: 'warning',
      title: title || 'Warning',
      message,
      onConfirm
    });
  }, [showAlert]);

  // Convenience method for info alerts
  const showInfo = useCallback((message, title, onConfirm) => {
    showAlert({
      type: 'info',
      title: title || 'Information',
      message,
      onConfirm
    });
  }, [showAlert]);

  // Convenience method for confirmation dialogs
  const showConfirm = useCallback((message, onConfirm, onCancel, title, confirmText, cancelText) => {
    showAlert({
      type: 'confirm',
      title: title || 'Confirm Action',
      message,
      confirmText: confirmText || 'Confirm',
      cancelText: cancelText || 'Cancel',
      onConfirm,
      onCancel
    });
  }, [showAlert]);

  const contextValue = {
    showAlert,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
    closeAlert
  };

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      <AlertModal
        open={alertState.open}
        onClose={closeAlert}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        onConfirm={alertState.onConfirm}
        onCancel={alertState.onCancel}
        showCancel={alertState.showCancel}
      />
    </AlertContext.Provider>
  );
};

/**
 * useAlert Hook
 * 
 * Custom hook to access alert functionality
 * 
 * @returns {Object} Alert methods
 * 
 * @example
 * const alert = useAlert();
 * 
 * // Show success message
 * alert.showSuccess("Operation completed successfully!");
 * 
 * // Show error message
 * alert.showError("Failed to save data. Please try again.");
 * 
 * // Show warning
 * alert.showWarning("This action cannot be undone.");
 * 
 * // Show info
 * alert.showInfo("Please save your work before proceeding.");
 * 
 * // Show confirmation dialog
 * alert.showConfirm(
 *   "Are you sure you want to delete this item?",
 *   () => { console.log("Confirmed"); },
 *   () => { console.log("Cancelled"); }
 * );
 * 
 * // Show custom alert
 * alert.showAlert({
 *   type: 'warning',
 *   title: 'Custom Title',
 *   message: 'Custom message here',
 *   confirmText: 'Got it',
 *   onConfirm: () => { console.log("Confirmed"); }
 * });
 */
export const useAlert = () => {
  const context = useContext(AlertContext);
  
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  
  return context;
};

export default AlertProvider;

